import type { CSSProperties } from 'react';
import { formatRupees, rupeesToPaise } from '../../lib/money';
import { formatCountdown, type PromoForm } from './promotions';

/** What the preview needs to know about the chosen coin package. */
export interface PreviewPkg {
  name: string;
  /** "₹199" — the normal (pre-promo) price. */
  priceLabel: string;
  coins: number;
  /** Paise as the API's string — used to mirror the server's floored % OFF. */
  amountPaise?: string;
}

/** The same floored percent the backend derives (shared-types/coin-packages). */
function promoPercent(pkg: PreviewPkg, promoPaise: number | null): number | null {
  if (promoPaise == null || !pkg.amountPaise) return null;
  const amount = Number(pkg.amountPaise);
  if (!Number.isFinite(amount) || amount <= 0 || promoPaise >= amount) return null;
  const pct = Math.floor(((amount - promoPaise) * 100) / amount);
  return pct > 0 ? pct : null;
}

/** Mobile-app brand colors, used when the campaign sets none of its own. */
const PINK = '#FB3C7F';
const DARK_CARD = '#1C0D14';
const DARK_BLUE = '#101A33';

const phoneFrame: CSSProperties = {
  width: 280,
  margin: '0 auto',
  padding: '24px 14px',
  borderRadius: 32,
  border: '1px solid #3a3a3a',
  background: 'linear-gradient(180deg, #241019 0%, #120a0e 100%)',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

function color(custom: string, fallback: string): string {
  return custom.trim() || fallback;
}

/**
 * How the SELECTED package will look inside the app's recharge list while the
 * campaign is live: badge ribbon, % OFF, struck normal price, campaign price,
 * CTA and the per-user countdown slot. A sketch of the app's card, not a
 * pixel copy — the real card is the app's PackageStoreCard.
 */
function WalletCardPreview({
  pkg,
  promoPaise,
  badge,
  cta,
  countdownSec,
  accent,
}: {
  pkg: PreviewPkg;
  promoPaise: number;
  badge: string;
  cta: string;
  countdownSec: number | null;
  accent: string;
}) {
  const pct = promoPercent(pkg, promoPaise);
  return (
    <div data-testid="promo-wallet-card-preview" style={{ marginTop: 14 }}>
      <div style={{ fontSize: 10, color: '#9b96a0', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
        In the recharge list
      </div>
      <div
        style={{
          border: `1.5px solid ${accent}`,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #1A0B15 0%, #2E0D1A 100%)',
          boxShadow: `0 0 14px ${accent}55`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'inline-block',
            background: `linear-gradient(90deg, ${accent}, #FF7131)`,
            color: '#fff',
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: 0.8,
            padding: '2px 12px 2px 8px',
            clipPath: 'polygon(0 0, 100% 0, calc(100% - 6px) 100%, 0 100%)',
          }}
        >
          ★ {badge.toUpperCase()}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>
              {pkg.coins.toLocaleString('en-IN')}{' '}
              <span style={{ fontSize: 10, fontWeight: 600, color: '#E6E4EA' }}>Coins</span>
            </div>
            <div style={{ fontSize: 10, color: '#908F95' }}>{pkg.name}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {pct != null && (
              <div
                style={{
                  display: 'inline-block',
                  background: '#FB1456',
                  color: '#fff',
                  borderRadius: 5,
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 6px',
                  marginBottom: 3,
                }}
              >
                {pct}% OFF
              </div>
            )}
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 17 }}>
              {formatRupees(promoPaise)}{' '}
              <span style={{ color: '#F8BB12', fontSize: 11, fontWeight: 600, textDecoration: 'line-through' }}>
                {pkg.priceLabel}
              </span>
            </div>
          </div>
          <div
            style={{
              background: `linear-gradient(90deg, ${accent}, #FF7131)`,
              color: '#fff',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              padding: '5px 10px',
              whiteSpace: 'nowrap',
            }}
          >
            {cta} ›
          </div>
        </div>
        {countdownSec != null && countdownSec > 0 && (
          <div style={{ padding: '0 10px 7px', fontSize: 10, fontWeight: 700, color: '#F8BB12' }}>
            ⏳ Ends in {formatCountdown(countdownSec)} (each user's own 7-day style window)
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A phone-style render of what the user will see, echoing the app's dark
 * theme. Pure presentation: everything comes from the draft form (+ the
 * chosen package); nothing is fetched here.
 */
export function PromoPreview({ form, pkg }: { form: PromoForm; pkg?: PreviewPkg | null }) {
  const d = form.design;
  const accent = color(form.accentColor, PINK);
  const textColor = color(form.textColor, '#FFFFFF');
  const buttonColor = color(form.buttonColor, accent);
  // No placeholder copy. The preview has to be honest about an empty field:
  // the app draws nothing there, so neither does this.
  const title = d.showTitle ? form.title.trim() : '';
  const message = d.showMessage ? form.message.trim() : '';
  const subtitle = d.showSubtitle ? form.subtitle.trim() : '';
  const titleColor = color(d.titleColor, textColor);
  const messageColor = color(d.messageColor, textColor);
  const subtitleColor = color(d.subtitleColor, textColor);
  const priceColor = color(d.priceColor, accent);
  // The price block is generated copy: shown only when asked for.
  const showPkg = d.showPackage;

  const closeBtn = form.showClose && (
    <span style={{ position: 'absolute', top: 8, right: 12, color: textColor, opacity: 0.6, fontSize: 14 }}>✕</span>
  );
  // Mirrors the app: a popup the user cannot otherwise close (acknowledgement,
  // or no dismiss link and no ✕) always keeps a button, falling back to a
  // built-in label. Every other popup shows the admin's words or no button.
  const trapped = form.requireAck || (!form.allowDismiss && !form.showClose);
  const ctaOrNothing = (mandatory: string) => {
    const typed = form.ctaText.trim();
    if (trapped) return ctaButton(typed || mandatory);
    if (!d.showCta || typed === '') return null;
    return ctaButton(typed);
  };
  const ctaButton = (label: string) => (
    <div
      style={{
        background: buttonColor,
        color: color(d.ctaTextColor, '#FFFFFF'),
        borderRadius: 999,
        padding: '8px 0',
        textAlign: 'center',
        fontWeight: 700,
        fontSize: 13,
        marginTop: 12,
      }}
    >
      {label}
    </div>
  );
  const dismissLink = d.showDismiss && form.dismissText.trim() && (
    <div
      style={{
        textAlign: 'center',
        marginTop: 8,
        fontSize: 12,
        color: color(d.dismissTextColor, textColor),
        opacity: d.dismissTextColor ? 1 : 0.6,
      }}
    >
      {form.dismissText.trim()}
    </div>
  );
  // Sized the way the phone sizes it: the admin's height, width share and fit.
  const image = form.imageUrl && (
    <img
      src={form.imageUrl}
      alt=""
      style={{
        width: d.imageWidthPercent != null ? `${d.imageWidthPercent}%` : '100%',
        display: 'block',
        margin: '0 auto 10px',
        borderRadius: 16,
        height: d.imageHeight != null ? Math.round(d.imageHeight * 0.45) : undefined,
        maxHeight: d.imageHeight != null ? undefined : 110,
        objectFit: d.imageFit === 'cover' ? 'cover' : 'contain',
      }}
    />
  );

  // An image turns a POPUP into a banner: the picture fills the card and the
  // text sits on top of it, exactly as the app draws it. Keeping the rule in
  // one place is what makes this preview trustworthy.
  if (form.imageUrl && form.placement === 'popup') {
    const align = form.bannerTextAlign;
    const pos = form.bannerTextPos;
    const justify = pos === 'top' ? 'flex-start' : pos === 'center' ? 'center' : 'flex-end';
    const scrim =
      pos === 'top'
        ? 'linear-gradient(180deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.35) 42%, rgba(0,0,0,0) 72%)'
        : pos === 'center'
          ? 'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.18) 100%)'
          : 'linear-gradient(0deg, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.42) 40%, rgba(0,0,0,0) 72%)';
    const hasText = title !== '' || message !== '' || subtitle !== '';
    const promoPaise = form.promoAmountRupees == null ? null : rupeesToPaise(form.promoAmountRupees);
    return (
      <div style={phoneFrame} data-testid="promo-preview">
        <div
          data-testid="promo-banner-preview"
          style={{
            position: 'relative',
            borderRadius: 20,
            overflow: 'hidden',
            minHeight: 300,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: justify,
            backgroundImage: `url(${form.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Image-only campaigns keep the picture clean: no shade, no text. */}
          {form.bannerOverlay && hasText && (
            <div style={{ position: 'absolute', inset: 0, background: scrim }} />
          )}
          {closeBtn}
          {hasText && (
            <div
              style={{
                position: 'relative',
                padding: 16,
                textAlign: align,
                // The app shadows every line it draws onto the picture, so the
                // text stays readable even with the gradient switched off.
                // Mirrored here or this preview would understate legibility.
                textShadow: '0 0 8px rgba(0,0,0,0.6)',
              }}
            >
              {title && (
                <div style={{ color: textColor, fontSize: 19, fontWeight: 800, lineHeight: 1.2 }}>
                  {title}
                </div>
              )}
              {message && (
                <div style={{ color: textColor, opacity: 0.85, fontSize: 12.5, marginTop: 6, lineHeight: 1.35 }}>
                  {message}
                </div>
              )}
              {showPkg && pkg && form.type === 'recharge_offer' && (
                <div style={{ marginTop: 8, color: accent, fontSize: 24, fontWeight: 900 }}>
                  {promoPaise != null ? formatRupees(promoPaise) : pkg.priceLabel}{' '}
                  {promoPaise != null && (
                    <span
                      style={{
                        color: textColor,
                        opacity: 0.55,
                        fontSize: 13,
                        fontWeight: 600,
                        textDecoration: 'line-through',
                      }}
                    >
                      {pkg.priceLabel}
                    </span>
                  )}
                </div>
              )}
              {subtitle && (
                <div style={{ color: textColor, opacity: 0.6, fontSize: 11, marginTop: 6 }}>{subtitle}</div>
              )}
              <div style={{ textShadow: 'none' }}>
                {d.showCta && form.ctaText.trim() && ctaButton(form.ctaText.trim())}
              </div>
              {dismissLink}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (form.type === 'recharge_offer') {
    const promoPaise = form.promoAmountRupees == null ? null : rupeesToPaise(form.promoAmountRupees);
    return (
      <div style={phoneFrame} data-testid="promo-preview">
        <div
          style={{
            position: 'relative',
            background: color(form.bgColor, DARK_CARD),
            color: textColor,
            borderRadius: 24,
            padding: 16,
            overflow: 'hidden',
          }}
        >
          {closeBtn}
          {form.countdownSec != null && form.showCountdown && (
            <div
              style={{
                background: accent,
                color: '#fff',
                borderRadius: 10,
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 700,
                padding: '4px 8px',
                marginBottom: 10,
              }}
            >
              ⏳ Offer ends in {formatCountdown(form.countdownSec)}
            </div>
          )}
          {image}
          {title && <div style={{ fontWeight: 800, fontSize: 16, color: titleColor }}>{title}</div>}
          {subtitle && (
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, color: subtitleColor }}>{subtitle}</div>
          )}
          {message && (
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6, color: messageColor }}>{message}</div>
          )}
          {showPkg && pkg && (
            <div
              style={{
                marginTop: 10,
                border: `1px solid ${accent}`,
                borderRadius: 16,
                padding: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{pkg.name}</div>
                <div style={{ fontSize: 11, opacity: 0.75 }}>{pkg.coins.toLocaleString('en-IN')} coins</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {promoPaise != null && (
                  <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: 12, marginRight: 6 }}>
                    {pkg.priceLabel}
                  </span>
                )}
                <span style={{ color: priceColor, fontWeight: 800, fontSize: 15 }}>
                  {promoPaise != null ? formatRupees(promoPaise) : pkg.priceLabel}
                </span>
              </div>
            </div>
          )}
          {ctaOrNothing('Recharge now')}
          {dismissLink}
        </div>
        {pkg && promoPaise != null && (
          <WalletCardPreview
            pkg={pkg}
            promoPaise={promoPaise}
            badge={subtitle || title}
            cta={form.ctaText.trim() || 'Buy Now'}
            countdownSec={form.showCountdown ? form.countdownSec : null}
            accent={accent}
          />
        )}
      </div>
    );
  }

  if (form.type === 'bonus_popup') {
    const bonusLabel =
      form.bonusType === 'percent' && form.bonusValue != null
        ? `+${form.bonusValue}% EXTRA`
        : form.bonusType === 'fixed' && form.bonusValue != null
          ? `+${form.bonusValue.toLocaleString('en-IN')} COINS`
          : 'BONUS';
    return (
      <div style={phoneFrame} data-testid="promo-preview">
        <div
          style={{
            position: 'relative',
            background: color(form.bgColor, DARK_BLUE),
            color: textColor,
            borderRadius: 24,
            padding: 16,
            textAlign: 'center',
          }}
        >
          {closeBtn}
          {image}
          <div
            style={{
              display: 'inline-block',
              background: accent,
              color: '#fff',
              borderRadius: 999,
              padding: '4px 14px',
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            {bonusLabel}
          </div>
          {title && (
            <div style={{ fontWeight: 800, fontSize: 16, marginTop: 10, color: titleColor }}>{title}</div>
          )}
          {subtitle && (
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, color: subtitleColor }}>{subtitle}</div>
          )}
          {message && (
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6, color: messageColor }}>{message}</div>
          )}
          {ctaOrNothing('Claim bonus')}
          {dismissLink}
        </div>
      </div>
    );
  }

  if (form.type === 'call_warning') {
    return (
      <div style={phoneFrame} data-testid="promo-preview">
        <div
          style={{
            position: 'relative',
            background: color(form.bgColor, DARK_CARD),
            color: textColor,
            borderRadius: 24,
            padding: 16,
            textAlign: 'center',
            border: `1px solid ${accent}`,
          }}
        >
          {closeBtn}
          {image}
          <div style={{ fontSize: 28 }}>⚠️</div>
          {title && (
            <div style={{ fontWeight: 800, fontSize: 16, marginTop: 6, color: titleColor }}>{title}</div>
          )}
          {subtitle && (
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, color: subtitleColor }}>{subtitle}</div>
          )}
          {message && (
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6, textAlign: 'left', color: messageColor }}>
              {message}
            </div>
          )}
          {ctaOrNothing('I Agree')}
          {dismissLink}
        </div>
      </div>
    );
  }

  // safety_banner — a light rounded card sitting over the home feed.
  return (
    <div style={phoneFrame} data-testid="promo-preview">
      <div
        style={{
          position: 'relative',
          background: color(form.bgColor, '#FFFFFF'),
          color: color(form.textColor, '#1C0D14'),
          borderRadius: 24,
          padding: 14,
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        }}
      >
        {closeBtn}
        {form.imageUrl ? (
          <img src={form.imageUrl} alt="" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 22 }}>🛡</span>
        )}
        <div style={{ paddingRight: form.showClose ? 16 : 0 }}>
          {title && <div style={{ fontWeight: 800, fontSize: 13, color: titleColor }}>{title}</div>}
          {subtitle && (
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 1, color: subtitleColor }}>{subtitle}</div>
          )}
          {message && (
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3, color: messageColor }}>{message}</div>
          )}
          {d.showCta && form.ctaText.trim() && (
            <div style={{ color: accent, fontWeight: 700, fontSize: 12, marginTop: 6 }}>{form.ctaText.trim()} ›</div>
          )}
        </div>
      </div>
    </div>
  );
}
